import { useState, useEffect, useMemo, Component } from 'react';
import { startResize } from '../utils/useResize';
import { fetchLinearTickers, tickersMeta, resolveBybitSymbol } from '../utils/bybit';
import { openAlertDraft, multiplierOf } from '../utils/alerts';
import { fmtPrice, fmtPct, fmtCompact } from '../utils/fmt';
import HomeModule, { CoinGeckoMark, trackHome } from './home/HomeModule';
import MarketState from './home/MarketState';
import HomeMovers from './home/HomeMovers';
import UpcomingEvents from './home/UpcomingEvents';
import HomeFunding from './home/HomeFunding';
import HomeTrending from './home/HomeTrending';
import { TICKER_EVENT, tickerOn } from '../utils/homeSettings';
import './DashboardTab.css';

// ── Constants ──────────────────────────────────────────────────────────────────

// ── Helpers ────────────────────────────────────────────────────────────────────

// Közös magyar formázás (utils/fmt.js)
const fmtPriceShort = (p) => fmtPrice(p);
const fmtChg = (c, dec = 2) => fmtPct(c, dec);

function chgColor(c) {
  return c == null ? 'var(--text2)' : c >= 0 ? 'var(--positive)' : 'var(--negative)';
}

function Skel({ h = 16, w = '100%' }) {
  return <div className="db-skel" style={{ height: h, width: w }} />;
}

// ── Selected coin header ──────────────────────────────────────────────────────
// The coin selected anywhere (watchlist, search, Top 8…) — the same coin as the
// chart below. Without CoinGecko market data the price comes from Bybit.

function useBybitFallback(coin) {
  const [fb, setFb] = useState(null);
  const needs = !!coin && !(coin.current_price > 0);
  useEffect(() => {
    if (!needs) { setFb(null); return; }
    let cancelled = false;
    fetchLinearTickers().then(map => {
      const sym = resolveBybitSymbol(coin.symbol, null, map);
      const t = sym && map[sym];
      if (cancelled) return;
      if (!t) { setFb({ missing: true }); return; }
      const mult = multiplierOf(sym);
      const price = parseFloat(t.lastPrice) / mult;
      const pct = parseFloat(t.price24hPcnt) * 100;
      setFb({ symbol: sym, price: Number.isFinite(price) ? price : null, chg24: Number.isFinite(pct) ? pct : null });
    }).catch(() => { if (!cancelled) setFb({ missing: true }); });
    return () => { cancelled = true; };
  }, [needs, coin?.id, coin?.symbol]); // eslint-disable-line react-hooks/exhaustive-deps
  return fb;
}

function CoinHeader({ coin, meta, user, expectedMs, onRetry, error }) {
  const fb = useBybitFallback(coin);
  if (!coin) {
    return error
      ? <HomeModule id="coin_header" user={user} title="Kiválasztott coin" source="CoinGecko" attr={<CoinGeckoMark />} error errorText="A CoinGecko most nem elérhető" onRetry={onRetry} className="hm-coin-skel"><Skel h={60} /></HomeModule>
      : <div className="db-sec hm-coin-skel"><Skel h={110} /></div>;
  }

  const fromBybit = !(coin.current_price > 0);
  const price = fromBybit ? fb?.price : coin.current_price;
  const chg24 = fromBybit ? fb?.chg24 : coin.price_change_percentage_24h;
  const chg1h = fromBybit ? null : coin.price_change_percentage_1h_in_currency;
  const chg7d = fromBybit ? null : coin.price_change_percentage_7d_in_currency;
  const isPos = (chg24 ?? 0) >= 0;
  const sym = (coin.symbol || '').toUpperCase();
  const bm = fromBybit ? tickersMeta() : null;

  return (
    <HomeModule
      id="coin_header" user={user} className="hm-coin"
      title={<span className="hm-coin-title">
        {coin.image || coin.thumb
          ? <img src={coin.image || coin.thumb} alt="" className="db-btc-logo" />
          : <span className="db-btc-logo db-btc-logo--txt" aria-hidden="true">{sym.slice(0, 3)}</span>}
        <span className="db-btc-name">{coin.name || sym}<span className="db-btc-sym">{sym}</span></span>
        {coin.market_cap_rank ? <span className="db-btc-rank-lbl">Rang: {coin.market_cap_rank}.</span> : null}
      </span>}
      right={
        <button type="button" className="db-coin-alert"
          onClick={() => { trackHome(user, 'home_module_interaction', { module: 'coin_header', action: 'alert' }); openAlertDraft({ kind: 'price', coin, source: 'home' }); }}>
          🔔 Riasztás
        </button>
      }
      source={fromBybit ? `${fb?.symbol ? `Bybit (${fb.symbol})` : 'Bybit'} · ${fb?.missing ? 'ehhez a coinhoz nincs ár' : 'CoinGecko-adat nélkül'}` : 'CoinGecko'}
      at={fromBybit ? bm?.at : meta?.at} expectedMs={fromBybit ? 60e3 : expectedMs} stale={fromBybit ? bm?.stale : meta?.stale}
      errorText={meta?.error || 'A CoinGecko most nem elérhető'} onRetry={onRetry} error={(error && !fromBybit && !meta?.at) || (!fromBybit && !!meta?.error)}
    >
      <div className="db-btc-price num" style={{ color: price == null ? 'var(--text3)' : isPos ? 'var(--positive)' : 'var(--negative)' }}>
        {price != null ? fmtPriceShort(price) : (fromBybit && !fb ? '…' : '—')}
      </div>
      <div className="db-btc-changes num">
        {[['1 ó', chg1h], ['24 ó', chg24], ['7 nap', chg7d]].map(([lbl, val]) => (
          <span key={lbl} style={{ color: chgColor(val) }}>
            <span className="db-chg-lbl">{lbl}</span> {fmtChg(val)}
          </span>
        ))}
        {!fromBybit && <span className="hm-coin-attr"><CoinGeckoMark /></span>}
      </div>
    </HomeModule>
  );
}

// ── TradingView Advanced Chart ────────────────────────────────────────────────

const TV_INTERVALS = [
  { label: '1H', value: '60'  },
  { label: '4H', value: '240' },
  { label: '1D', value: 'D'   },
  { label: '1W', value: 'W'   },
];

class TVErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div className="db-tv-error">
          <div className="db-tv-error-msg">Grafikon betöltési hiba</div>
          <a
            href={`https://www.tradingview.com/chart/?symbol=BYBIT:${this.props.symbol || 'BTC'}USDT`}
            target="_blank"
            rel="noopener noreferrer"
            className="db-tv-error-btn"
          >
            Megnyitás TradingView-on →
          </a>
        </div>
      );
    }
    return this.props.children;
  }
}

function TVWidget({ symbol, interval }) {
  // Stable ID per symbol+interval — only regenerates when those change, not on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const containerId = useMemo(() => `tv_${symbol}_${interval}_${Date.now()}`, [symbol, interval]);

  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container) return;

    container.innerHTML = '';

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.async = true;
    script.crossOrigin = 'anonymous';

    const config = {
      autosize: true,
      symbol: `BYBIT:${symbol}USDT`,
      interval,
      timezone: 'Etc/UTC',
      theme: 'dark',
      style: '1',
      locale: 'hu',
      backgroundColor: 'rgba(5, 10, 20, 1)',
      hide_top_toolbar: false,
      hide_legend: false,
      save_image: false,
      hide_volume: false,
      support_host: 'https://www.tradingview.com',
    };

    script.innerHTML = JSON.stringify(config);

    try {
      container.appendChild(script);
    } catch (_) {}

    return () => {
      try { container.innerHTML = ''; } catch (_) {}
    };
  }, [symbol, interval, containerId]);

  return (
    <div className="db-tv-outer">
      <div className="tradingview-widget-container" style={{ height: '100%', width: '100%' }}>
        <div id={containerId} style={{ height: '100%', width: '100%' }} />
      </div>
    </div>
  );
}

function TradingViewChart({ symbol = 'BTC', user, onModel, model }) {
  const [tvInterval, setTVInterval] = useState('D');
  const [menu, setMenu] = useState(false);
  const sym = (symbol || 'BTC').toUpperCase();
  const tvUrl = `https://www.tradingview.com/chart/?symbol=BYBIT:${sym}USDT&ref=ddrjano`;

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [menu]);

  const pickInterval = (v) => { setTVInterval(v); trackHome(user, 'home_module_interaction', { module: 'chart', action: `interval_${v}` }); };
  const pickModel = (m) => { setMenu(false); onModel?.(model === m ? null : m); trackHome(user, 'home_module_interaction', { module: 'chart', action: `model_${m}` }); };

  return (
    <HomeModule id="chart" user={user} className="db-tv-sec"
      title={`${sym}/USDT · Grafikon`}
      source="TradingView"
      right={
        <div className="db-tv-tools">
          <div className="db-tv-intervals">
            {TV_INTERVALS.map(i => (
              <button key={i.value} className={`db-tv-btn${tvInterval === i.value ? ' active' : ''}`} onClick={() => pickInterval(i.value)}>
                {i.label}
              </button>
            ))}
          </div>
          <div className="db-tv-models">
            <button type="button" className={`db-tv-btn${model ? ' active' : ''}`} aria-haspopup="menu" aria-expanded={menu}
              onClick={(e) => { e.stopPropagation(); setMenu(m => !m); }}>
              Modellek ▾
            </button>
            {menu && (
              <div className="db-tv-menu" role="menu" onClick={e => e.stopPropagation()}>
                <button type="button" role="menuitemcheckbox" aria-checked={model === 'rainbow'} onClick={() => pickModel('rainbow')}>
                  {model === 'rainbow' ? '✓ ' : ''}🌈 Bitcoin Rainbow chart
                </button>
              </div>
            )}
          </div>
        </div>
      }
    >
      <TVErrorBoundary key={`${sym}-${tvInterval}`} symbol={sym}>
        <TVWidget symbol={sym} interval={tvInterval} />
      </TVErrorBoundary>
      <div className="db-tv-footer">
        <a href={tvUrl} target="_blank" rel="noopener noreferrer" className="db-tv-open-btn">TradingView-on megnyitom →</a>
      </div>
    </HomeModule>
  );
}

// ── Bitcoin Rainbow Chart ─────────────────────────────────────────────────────

// Band boundary formula: log10(price) = 2.66167155 * ln(days_since_genesis) - 17.9183 + C
// C values calibrated so Jan 2026 boundaries match: $2K,$10K,$25K,$45K,$70K,$100K,$150K,$250K,$500K,$1M
const RC_C_BANDS = [-2.0225,-1.3235,-0.9256,-0.6703,-0.4784,-0.3235,-0.1474,0.0744,0.3755,0.6765,0.9775];

const RC_ZONES = [
  { label:'Alulértékelt',       color:'#006600', textColor:'#fff' },
  { label:'Halmozz fel',        color:'#00cc00', textColor:'#000' },
  { label:'Vásárolj!',          color:'#33ff00', textColor:'#000' },
  { label:'Még olcsó',          color:'#99ff00', textColor:'#000' },
  { label:'Semleges - Tartsd',  color:'#ffff00', textColor:'#000' },
  { label:'FOMO',               color:'#ff9900', textColor:'#000' },
  { label:'FOMO erősen',        color:'#ff6600', textColor:'#fff' },
  { label:'Eladj!',             color:'#ff0000', textColor:'#fff' },
  { label:'Maximum buborék',    color:'#7f0000', textColor:'#fff' },
];

function rcBoundaryPrice(yearFraction, cBand) {
  const genesis = new Date(2009, 0, 3);
  const target  = new Date(Math.floor(yearFraction), Math.round((yearFraction % 1) * 12), 1);
  const days = Math.round((target - genesis) / 86400000);
  if (days <= 0) return 0.01;
  return Math.pow(10, 2.66167155 * Math.log(days) - 17.9183 + cBand);
}

const RC_YEARS = [2012,2013,2014,2015,2016,2017,2018,2019,2020,2021,2022,2023,2024,2025,2026,2027,2028];

/* Current date as a fractional year (e.g. 2026.76), so the "now" marker and the
   zone lookup move with time instead of being pinned to one date. */
function yearFraction(ms = Date.now()) {
  const d = new Date(ms);
  const start = Date.UTC(d.getUTCFullYear(), 0, 1);
  const end   = Date.UTC(d.getUTCFullYear() + 1, 0, 1);
  return d.getUTCFullYear() + (ms - start) / (end - start);
}

function RainbowChart({ btcCoin, meta, onClose }) {
  const open = true;
  const currentPrice = btcCoin?.current_price ?? null;

  const W = 820, H = 300;
  const PL = 52, PR = 115, PT = 10, PB = 28;
  const cW = W - PL - PR, cH = H - PT - PB;
  const YEAR_MIN = 2012, YEAR_MAX = 2028;
  const LOG_MIN = -1, LOG_MAX = 7;

  function xOf(yr)    { return PL + (yr - YEAR_MIN) / (YEAR_MAX - YEAR_MIN) * cW; }
  function yOf(price) {
    if (!price || price <= 0) return PT + cH;
    const lp = Math.max(LOG_MIN, Math.min(LOG_MAX, Math.log10(price)));
    return PT + cH - (lp - LOG_MIN) / (LOG_MAX - LOG_MIN) * cH;
  }

  // Compute top polygon points (from C_BANDS[i+1]) and bottom (C_BANDS[i]) per zone
  const zonePolys = RC_ZONES.map((_, i) => {
    const top = RC_YEARS.map(y => `${xOf(y).toFixed(1)},${yOf(rcBoundaryPrice(y, RC_C_BANDS[i+1])).toFixed(1)}`);
    const bot = [...RC_YEARS].reverse().map(y => `${xOf(y).toFixed(1)},${yOf(rcBoundaryPrice(y, RC_C_BANDS[i])).toFixed(1)}`);
    return [...top, ...bot].join(' ');
  });

  // Top cap polygon (above zone 9 → chart top edge)
  const topCapPts = [
    ...RC_YEARS.map(y => `${xOf(y).toFixed(1)},${PT}`),
    ...[...RC_YEARS].reverse().map(y => `${xOf(y).toFixed(1)},${yOf(rcBoundaryPrice(y, RC_C_BANDS[9])).toFixed(1)}`),
  ].join(' ');

  const currentY = currentPrice != null ? yOf(currentPrice) : null;

  // Find current zone (using today's boundaries)
  const nowYr = yearFraction();
  let currentZone = null;
  if (currentPrice != null) {
    for (let i = RC_ZONES.length - 1; i >= 0; i--) {
      if (currentPrice >= rcBoundaryPrice(nowYr, RC_C_BANDS[i])) { currentZone = i; break; }
    }
    if (currentZone === null) currentZone = 0;
  }

  const Y_TICKS = [
    { p: 0.1, lbl: '0,1 $' }, { p: 1, lbl: '1 $' }, { p: 10, lbl: '10 $' },
    { p: 100, lbl: '100 $' }, { p: 1e3, lbl: '1 ezer $' }, { p: 1e4, lbl: '10 ezer $' },
    { p: 1e5, lbl: '100 ezer $' }, { p: 1e6, lbl: '1 M $' }, { p: 1e7, lbl: '10 M $' },
  ];
  const X_TICKS = [2012, 2014, 2016, 2018, 2020, 2022, 2024, 2026, 2028];

  return (
    <div className="rc-wrap">
      <button className="rc-toggle" onClick={onClose}>✕ Rainbow chart bezárása</button>
      {open && (
        <div className="rc-card">
          <div className="rc-header">
            <div className="rc-title">🌈 Bitcoin Rainbow Chart — Historikus Értékelési Modell</div>
            <div className="rc-subtitle">Logaritmikus regresszió alapú ármodell — nem pénzügyi tanácsadás</div>
          </div>

          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
            {/* Zone bands */}
            {zonePolys.map((pts, i) => (
              <polygon key={i} points={pts} fill={RC_ZONES[i].color} opacity="0.82" />
            ))}
            {/* Above-zone-9 fill */}
            <polygon points={topCapPts} fill="#3d0000" opacity="0.5" />

            {/* Zone labels */}
            {RC_ZONES.map((z, i) => {
              const midC = (RC_C_BANDS[i] + RC_C_BANDS[i + 1]) / 2;
              const labelY = yOf(rcBoundaryPrice(2027.5, midC));
              if (labelY < PT || labelY > PT + cH) return null;
              return (
                <text key={i} x={xOf(2028.1)} y={labelY + 3.5} fontSize="8.5" fill={z.color} fontWeight="700">
                  {z.label}
                </text>
              );
            })}

            {/* Current price line */}
            {currentY != null && currentY >= PT && currentY <= PT + cH && (
              <>
                <line x1={PL} y1={currentY} x2={xOf(2028.05)} y2={currentY}
                  stroke="#fff" strokeWidth="1.5" strokeDasharray="5,4" />
                <text x={xOf(2028.1)} y={currentY + 3.5} fontSize="8.5" fill="#fff" fontWeight="700">
                  Most: {currentPrice >= 1000
                    ? fmtCompact(currentPrice)
                    : fmtPrice(currentPrice)}
                </text>
              </>
            )}

            {/* "Now" vertical marker */}
            {(() => { const nx = xOf(nowYr); return (
              <line x1={nx} y1={PT} x2={nx} y2={PT+cH}
                stroke="rgba(255,255,255,0.25)" strokeWidth="1" strokeDasharray="3,3" />
            ); })()}

            {/* Y axis */}
            <line x1={PL} y1={PT} x2={PL} y2={PT+cH} stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
            {Y_TICKS.map(({ p, lbl }) => {
              const y = yOf(p);
              if (y < PT || y > PT + cH) return null;
              return (
                <g key={lbl}>
                  <line x1={PL-3} y1={y} x2={PL} y2={y} stroke="rgba(255,255,255,0.25)" strokeWidth="1" />
                  <text x={PL-5} y={y+3} textAnchor="end" fontSize="7.5" fill="rgba(255,255,255,0.45)">{lbl}</text>
                  <line x1={PL} y1={y} x2={PL+cW} y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
                </g>
              );
            })}

            {/* X axis */}
            <line x1={PL} y1={PT+cH} x2={PL+cW} y2={PT+cH} stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
            {X_TICKS.map(yr => {
              const x = xOf(yr);
              return (
                <g key={yr}>
                  <line x1={x} y1={PT+cH} x2={x} y2={PT+cH+3} stroke="rgba(255,255,255,0.25)" strokeWidth="1" />
                  <text x={x} y={PT+cH+12} textAnchor="middle" fontSize="7.5" fill="rgba(255,255,255,0.45)">{yr}</text>
                </g>
              );
            })}
          </svg>

          {currentZone !== null && (
            <div className="rc-zone-badge" style={{
              borderColor: RC_ZONES[currentZone].color,
              background:  RC_ZONES[currentZone].color + '18',
            }}>
              📊 Jelenleg: <strong style={{ color: RC_ZONES[currentZone].color }}>
                {RC_ZONES[currentZone].label}
              </strong>
              {' — '}
              {currentZone <= 1 ? 'Historikusan vétel területe'
                : currentZone <= 3 ? 'Egészséges bika piac'
                : currentZone <= 5 ? 'Eufória / FOMO terület'
                : 'Historikusan buborék terület'}
            </div>
          )}

          <div className="rc-disclaimer">
            ⚠️ Ez egy historikus, logaritmikus regressziós modell szemléltetési célból (BTC ár: CoinGecko). Nem pénzügyi tanácsadás.
            {'  '}Eredeti Rainbow Chart:{' '}
            <a href="https://www.blockchaincenter.net/en/bitcoin-rainbow-chart/" target="_blank" rel="noreferrer" style={{ color:'var(--accent)' }}>
              blockchaincenter.net
            </a>
          </div>
          <div className="hm-attr-row"><CoinGeckoMark /></div>
        </div>
      )}
    </div>
  );
}

// ── Price Ticker ──────────────────────────────────────────────────────────────
// Alapból kikapcsolva (3a); a fejléc 💾-menüjében kapcsolható (w3k_ticker_on).


function useTickerSetting() {
  const [on, setOn] = useState(tickerOn);
  useEffect(() => {
    const h = () => setOn(tickerOn());
    window.addEventListener(TICKER_EVENT, h);
    return () => window.removeEventListener(TICKER_EVENT, h);
  }, []);
  return on;
}

function PriceTicker({ coins }) {
  const items = coins.slice(0, 30);
  const content = items.map((c, i) => {
    const chg   = c.price_change_percentage_24h ?? 0;
    const isPos = chg >= 0;
    return (
      <span key={c.id} className="db-ticker-item">
        <img src={c.image} alt="" style={{ width:14, height:14, borderRadius:'50%', verticalAlign:'middle' }}
          onError={e => { e.target.style.display='none'; }} />
        <span className="db-ticker-sym">{c.symbol.toUpperCase()}</span>
        <span className="db-ticker-price num">{fmtPriceShort(c.current_price)}</span>
        <span className={`db-ticker-chg num ${isPos ? 'pos' : 'neg'}`}>{fmtChg(chg)}</span>
        {i < items.length - 1 && <span className="db-ticker-sep">◆</span>}
      </span>
    );
  });
  return (
    <div className="db-ticker-bar">
      <div className="db-ticker-track">{content}{content}</div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
// Bal: Piaci állapot + Funding · Közép: kiválasztott coin + grafikon (Modellek) ·
// Jobb: 24 órás mozgók, közelgő események, keresési népszerűség.

export default function DashboardTab({ coins, onSelectCoin, selectedCoin, marketsMeta, fearGreedData, trendingData, trendingMeta, user, refreshMs = {}, onRetry = {}, marketsError = false }) {
  const btcCoin = useMemo(() => coins.find(c => c.id === 'bitcoin'), [coins]);
  const chartSymbol = selectedCoin?.symbol ?? btcCoin?.symbol ?? 'BTC';
  const [model, setModel] = useState(null);
  const ticker = useTickerSetting();

  const [leftW, setLeftW] = useState(() => {
    const v = parseInt(localStorage.getItem('dashboard_col_left'));
    return isNaN(v) ? 280 : Math.min(480, Math.max(180, v));
  });
  const [rightW, setRightW] = useState(() => {
    const v = parseInt(localStorage.getItem('dashboard_col_right'));
    return isNaN(v) ? 300 : Math.min(480, Math.max(180, v));
  });

  return (
    <div className="db-root">
      <div className="db-main">

        {/* ── LEFT COLUMN ── */}
        <div className="db-col" style={{ width: leftW, flexShrink: 0 }}>
          <MarketState coins={coins} fearGreedData={fearGreedData} user={user} marketsMeta={marketsMeta} />
          <HomeFunding selectedCoin={selectedCoin} user={user} />
        </div>

        <div className="db-col-resize"
          onMouseDown={e => startResize({ e, current: leftW, min: 180, max: 480,
            onResize: v => { setLeftW(v); localStorage.setItem('dashboard_col_left', String(v)); } })} />

        {/* ── CENTER COLUMN ── */}
        <div className="db-col db-col-center" style={{ flex: 1 }}>
          <CoinHeader coin={selectedCoin ?? btcCoin} meta={marketsMeta} user={user} expectedMs={refreshMs.markets} onRetry={onRetry.markets} error={marketsError} />
          <TradingViewChart symbol={chartSymbol} user={user} model={model} onModel={setModel} />
          {model === 'rainbow' && <RainbowChart btcCoin={btcCoin} meta={marketsMeta} onClose={() => setModel(null)} />}
        </div>

        <div className="db-col-resize"
          onMouseDown={e => startResize({ e, current: rightW, min: 180, max: 480, invert: true,
            onResize: v => { setRightW(v); localStorage.setItem('dashboard_col_right', String(v)); } })} />

        {/* ── RIGHT COLUMN ── */}
        <div className="db-col" style={{ width: rightW, flexShrink: 0 }}>
          <HomeMovers coins={coins} meta={marketsMeta} user={user} onSelectCoin={onSelectCoin} expectedMs={refreshMs.markets} onRetry={onRetry.markets} error={marketsError} />
          <UpcomingEvents user={user} />
          <HomeTrending trendingData={trendingData} meta={trendingMeta} user={user} expectedMs={refreshMs.trending} onRetry={onRetry.trending} />
        </div>

      </div>

      {ticker && <PriceTicker coins={coins} />}
    </div>
  );
}
